<template>
    <div>
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Debtor List</h5>
          <p class="page-sub">Customers with outstanding balances</p>
        </div>
        <div class="page-actions">
  
          <!-- Search -->
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search debtors..." v-model="query" />
          </div>
  
          <!-- Export -->
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" href="#" @click="drawPdf(print_content, headerArrayColumn, 'l', 'Debtors', 'Debtor')">
                <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
              </a></li>
              <li><a class="dropdown-item" href="#" @click="reusableMixinToCSV('debtor.csv')">
                <font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV
              </a></li>
            </ul>
          </div>
  
        </div>
      </div>
  
      <!-- Data Card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted">Loading debtors...</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="filtersearch.length === 0" class="state-block">
          <div class="state-icon state-icon--green">
            <font-awesome-icon icon="check-circle" />
          </div>
          <p class="state-title">No debtors found</p>
          <p class="state-sub">
            {{ query ? 'No results match your search.' : 'All customers are fully paid up.' }}
          </p>
        </div>
  
        <!-- Table -->
        <template v-else>
  
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ filtersearch.length }}</strong>
              {{ query ? 'result(s)' : `of ${pagination.total ?? filtersearch.length}` }}
            </span>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th style="width:28%">Customer</th>
                  <th class="text-right">Total Due ({{ company?.currency?.symbol }})</th>
                  <th class="text-right">Amount Received ({{ company?.currency?.symbol }})</th>
                  <th class="text-right">Balance ({{ company?.currency?.symbol }})</th>
                  <th class="col-actions">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="debtor in filtersearch" :key="debtor.id">
  
                  <td>
                    <div class="user-cell">
                      <div class="user-avatar">{{ debtor?.customer?.customer_name?.charAt(0) }}</div>
                      <span class="product-name">{{ debtor?.customer?.customer_name }}</span>
                    </div>
                  </td>
  
                  <td class="text-right text-muted-cell">
                    {{ $filters.formatMoney(debtor.total_amount) }}
                  </td>
  
                  <td class="text-right text-muted-cell">
                    {{ $filters.formatMoney(debtor.amount_paid) }}
                  </td>
  
                  <td class="text-right">
                    <span class="balance-badge" :class="debtor.amount_owned > 0 ? 'balance-badge--due' : 'balance-badge--clear'">
                      {{ $filters.formatMoney(debtor.amount_owned) }}
                    </span>
                  </td>
  
                  <td class="col-actions">
                    <router-link
                      :to="$url('/user-debtor/' + debtor.customer_id)"
                      class="action-btn action-btn--view"
                      title="View debtor details"
                    >
                      <font-awesome-icon icon="eye" />
                    </router-link>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <div class="table-footer">
            <pagination
              :pagination="pagination"
              @prev="tableData.page = pagination.currentPage - 1; fetchData()"
              @next="tableData.page = pagination.currentPage + 1; fetchData()"
              v-if="details.length > 0"
            />
          </div>
  
        </template>
      </div>
  
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { reusableMixin } from '../../../lib/reusableMixin';
  
  export default {
    mixins: [reusableMixin],
  
    data() {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  
      return {
        print_content: [],
        headerArrayColumn: ['S/N','Customer Name','Total Amount Due','Total Amount Received','Total Balance'],
        details: [], loading: false, query: '',
        selectedModel: [], allSelected: false,
        tableData: { page: 1,
          start_date: sixMonthsAgo, end_date: new Date(),
          draw: 0, length: 10, search: '', column: 0,
          dir: 'desc', id: '', status: [0,1,2,3], query: '',
        },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast: useToast(), permission_status: null, crudLoading: false,
      };
    },
  
    computed: {
      filtersearch() {
        if (!this.query) return this.details;
        const q = this.query.toLowerCase();
        return this.details.filter(d =>
          d?.customer?.customer_name?.toLowerCase().includes(q) ||
          d?.total_amount?.toString().includes(q) ||
          d?.amount_paid?.toString().includes(q)
        );
      },
    },
  
    watch: {
      query() { if (!this.query) this.fetchData(); },
    },
  
    mounted() { this.fetchData(); this.getCompany(); },
  
    methods: {
      debtPaid(debtor) {
        return debtor?.debts?.reduce((sum, d) => parseFloat(sum) + parseFloat(d.amount_paid), 0) ?? 0;
      },
  
      balance(debtor) {
        return debtor.total_amount - this.debtPaid(debtor);
      },
  
      dataToPrint(details) {
        this.print_content = details.map((debtor, idx) => ({
          sn:            idx + 1,
          customer_name: debtor?.customer?.customer_name,
          total_amount:  debtor?.total_amount,
          amount_paid:   this.debtPaid(debtor),
          amount_owned:  this.balance(debtor),
        }));
      },
  
      fetchData(url = '/debtors') {
        this.loading = true; this.crudLoading = true;
        this.tableData.query = this.query;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            this.crudLoading = false; this.loading = false;
            this.details = response.data.data.data;
            this.dataToPrint(this.details);
            this.configPagination(response.data.data);
          })
          .catch(error => {
            this.crudLoading = false; this.loading = false;
            if (error?.response?.status == 403) {
              this.permission_status = 403;
              this.toast.error(error.response.data.message);
            }
          });
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  /* ── Search ── */
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:210px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .search-input::placeholder { color:#aab8cc; }
  
  /* ── Buttons ── */
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-outline::after { display:none !important; }
  
  /* ── Data Card ── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ── */
  .state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon  { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--green { background:rgba(22,163,74,.1); color:#16a34a; }
  .state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub   { font-size:13px; color:#7a90b0; margin:0; }
  .mt-3        { margin-top:12px; }
  
  /* ── Toolbar ── */
  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count   { font-size:13px; color:#7a90b0; }
  
  /* ── Table ── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:13px 16px; font-size:13.5px; vertical-align:middle; }
  .col-actions { width:60px; text-align:center; }
  .text-right      { text-align:right; }
  .text-muted-cell { color:#4a5e78; }
  
  /* ── User Cell ── */
  .user-cell   { display:flex; align-items:center; gap:10px; }
  .user-avatar { width:34px; height:34px; border-radius:50%; background:#004AAD; color:#fff; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; flex-shrink:0; text-transform:uppercase; }
  .product-name { font-weight:600; color:#1a2b45; }
  
  /* ── Balance Badge ── */
  .balance-badge      { display:inline-block; border-radius:6px; padding:3px 10px; font-size:13px; font-weight:700; border:1px solid transparent; }
  .balance-badge--due  { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
  .balance-badge--clear { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  
  /* ── Action Button ── */
  .action-btn   { width:32px; height:32px; border-radius:7px; border:1px solid #e4eaf2; display:flex; align-items:center; justify-content:center; font-size:13px; cursor:pointer; transition:background .15s; text-decoration:none; background:#fff; margin:0 auto; }
  .action-btn--view { color:#0d9488; }
  .action-btn--view:hover { background:#f0fdfb; border-color:#a7f3d0; }
  
  /* ── Footer ── */
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  /* ── Dropdown ── */
  .dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:170px; }
  .dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; }
  .dropdown-item:hover { background:#f4f7fb; }
  
  /* ── Responsive ── */
  @media(max-width:767px) {
    .page-header,.page-actions { flex-direction:column; align-items:stretch; }
    .search-input { width:100%; }
    .btn-outline { width:100%; justify-content:center; }
  }
  </style>